import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  absoluteUrl,
  organizationJsonLd,
  seoForPath,
} from "../content/seo";
import { SITE_NAME } from "../lib/site";

const JSON_LD_ID = "specops-jsonld";

function upsertMeta(
  attribute: "name" | "property",
  key: string,
  content: string,
) {
  const selector = `meta[${attribute}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(data: object | null) {
  const existing = document.getElementById(JSON_LD_ID);
  if (!data) {
    existing?.remove();
    return;
  }
  const el =
    existing ?? Object.assign(document.createElement("script"), { id: JSON_LD_ID });
  el.type = "application/ld+json";
  el.textContent = JSON.stringify(data);
  if (!existing) {
    document.head.appendChild(el);
  }
}

export function PageMeta() {
  const { pathname } = useLocation();
  const seo = seoForPath(pathname);
  const url = absoluteUrl(seo.path);
  const includeOrgSchema = seo.path === "/";

  useEffect(() => {
    document.title = seo.title;

    upsertMeta("name", "description", seo.description);
    upsertMeta(
      "name",
      "robots",
      seo.private ? "noindex, nofollow" : "index, follow",
    );

    upsertLink("canonical", url);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:locale", "en_US");

    upsertMeta("name", "twitter:card", "summary");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);

    upsertJsonLd(includeOrgSchema ? organizationJsonLd : null);
  }, [seo, url, includeOrgSchema]);

  return null;
}
